import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageSquare, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOME_CHIPS = [
  { key: 'won', label: 'Won', tone: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { key: 'follow_up_booked', label: 'Follow-up booked', tone: 'bg-teal-100 text-teal-800 border-teal-200' },
  { key: 'no_response', label: 'No response', tone: 'bg-slate-100 text-slate-600 border-slate-200' },
  { key: 'not_interested', label: 'Not interested', tone: 'bg-rose-100 text-rose-800 border-rose-200' },
  { key: 'untagged', label: 'Untagged', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
];

/**
 * "SMS Conversion" KPI card — of the SMS the team actually sent (real,
 * non-simulated Yabbit SMS), how many converted? Per agent + per site.
 * `compact` renders headline + site chips only (Sales Funnel variant).
 */
const SmsConversionCard = ({ domainId = null, days = 30, compact = false }) => {
  const d = /^\d+$/.test(String(days)) ? Number(days) : 30;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    p.set('days', String(d));
    axios
      .get(`${API}/analytics/sms-conversion?${p.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [domainId, d]);

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading SMS conversion…
      </div>
    );
  }
  if (!data || data.enabled === false) return null;

  const t = data.totals || {};
  const sent = t.sent || 0;
  const rate = t.conversion_pct ?? 0;
  const numTone = sent === 0 ? 'text-slate-400' : rate >= 20 ? 'text-emerald-700' : rate >= 8 ? 'text-amber-700' : 'text-rose-700';

  return (
    <div
      className="rounded-xl border border-purple-200 bg-purple-50/60 p-4 flex flex-col gap-2 min-w-[220px]"
      data-testid="sms-conversion-card"
    >
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-purple-700" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-700">SMS Conversion</p>
        <span className="ml-auto text-[10px] text-slate-500">last {d}d</span>
      </div>

      {sent === 0 ? (
        <p className="text-xs text-slate-500" data-testid="sms-conversion-empty">
          No real SMS sent in this window
          {(t.simulated || 0) > 0 && <span className="text-amber-600"> · {t.simulated} test-fire{t.simulated === 1 ? '' : 's'}</span>}
          {(t.gymsales_links || 0) > 0 && <span> · {t.gymsales_links} GymSales link{t.gymsales_links === 1 ? '' : 's'}</span>}
        </p>
      ) : (
        <>
          <div className="flex items-end gap-3">
            <p className={`text-3xl font-bold tabular-nums ${numTone}`} data-testid="sms-conversion-rate">
              {rate.toFixed(1)}%
            </p>
            <p className="text-[11px] text-slate-600 pb-1">
              <span className="font-semibold tabular-nums">{t.won}</span> won of{' '}
              <span className="font-semibold tabular-nums">{sent}</span> sent
              {(t.positive_pct ?? 0) > rate && (
                <span className="text-teal-700"> · {t.positive_pct.toFixed(1)}% incl. follow-ups booked</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {OUTCOME_CHIPS.map((c) => (
              <span key={c.key} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${c.tone}`} data-testid={`sms-conv-chip-${c.key}`}>
                {c.label} {t[c.key] || 0}
              </span>
            ))}
          </div>
        </>
      )}

      {(data.per_site || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t border-purple-100 pt-2" data-testid="sms-conversion-sites">
          {data.per_site.map((s) => (
            <span key={s.site} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-purple-100 text-slate-600">
              <span className="font-semibold">{s.site}</span> · {s.sent} sent · {s.won} won ({s.conversion_pct.toFixed(0)}%)
            </span>
          ))}
        </div>
      )}

      {!compact && (data.per_agent || []).length > 0 && (
        <table className="w-full text-[11px] mt-1" data-testid="sms-conversion-agent-table">
          <thead>
            <tr className="text-left text-[9px] uppercase tracking-wider text-slate-400 border-b border-purple-100">
              <th className="py-1 pr-2">Agent</th>
              <th className="py-1 pr-2">Site</th>
              <th className="py-1 pr-2 text-right">Sent</th>
              <th className="py-1 pr-2 text-right">Won</th>
              <th className="py-1 text-right">Conv %</th>
            </tr>
          </thead>
          <tbody>
            {data.per_agent.slice(0, 8).map((a) => (
              <tr key={a.agent_id} className="border-b border-purple-50 last:border-0" data-testid={`sms-conversion-agent-row-${a.agent_id}`}>
                <td className="py-1 pr-2 text-slate-700 font-medium">{a.name}</td>
                <td className="py-1 pr-2 text-slate-500">{a.site}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{a.sent}</td>
                <td className="py-1 pr-2 text-right tabular-nums text-emerald-700">{a.won}</td>
                <td className="py-1 text-right tabular-nums font-semibold">{a.conversion_pct.toFixed(0)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SmsConversionCard;
